import { useEffect, useRef } from 'react';
import { presenceActions } from '@/features/presence/presenceStore';
import { readCallActivity } from '../callWindow';
import { useDesktopEvent } from './useDesktopEvent';

/**
 * THE WORKSPACE'S HALF OF PRESENCE — the document that actually heartbeats,
 * learning what the call windows are doing.
 *
 * The shell keeps one row per live call window and hands over the AGGREGATE, so
 * this never has to know how many windows there are or which call each holds.
 * That is deliberate and load-bearing in two places:
 *
 *   • a call window closing cannot say "not in a call" on another one's behalf,
 *     which is exactly what the old `call-window-closed` handler did — and it
 *     could name a call that a DIFFERENT, live window had since taken over;
 *   • it folds into `shellFlags`, not `flags`, so it cannot clobber what THIS
 *     document is doing. The workspace may be in a Teams voice channel or a
 *     meeting of its own, and the resolved activity is the OR of the two.
 *
 * PULL AND PUSH, like the token broker. A workspace that reloads mid-call comes
 * back as a fresh document that missed every push, so it asks once on mount;
 * everything after that arrives as an event.
 *
 * A no-op in a browser and in a call window: the read resolves null and the
 * event never fires.
 */
export function useDesktopCallPresence(): void {
  /* A push that landed while the mount read was in flight is NEWER than the
     answer coming back. Without this a call answered in the first moments of a
     workspace reload could be overwritten by the empty aggregate that was true
     when the question was asked. */
  const pushed = useRef(false);

  useEffect(() => {
    let cancelled = false;
    void readCallActivity().then((activity) => {
      if (cancelled || pushed.current || activity === null) return;
      presenceActions.setShellFlags(activity);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useDesktopEvent('call-activity', (event) => {
    pushed.current = true;
    presenceActions.setShellFlags(event.activity);
  });
}
